import { DataEntry, LedgerAccount } from './types';

export const lookupAccount = (dataEntry: DataEntry): LedgerAccount | undefined => {
  if (!dataEntry?.journalEntry?.dataEntries) {
    return undefined; // Return undefined if dataEntries is not available
  }
  return dataEntry.journalEntry.dataEntries.find(entry => entry.id !== dataEntry.id)?.ledgerAccount;
};

export const hasSourceInfo = (
  account?: LedgerAccount,
): account is LedgerAccount & {
  financialInstitutionProduct: NonNullable<LedgerAccount['financialInstitutionProduct']>;
  accountLastFourDigits: string;
} => {
  return !!(
    account?.financialInstitutionProduct?.financialInstitution?.name &&
    account?.accountLastFourDigits
  );
};

const PERSONAL_BUSINESS_LABEL = '(Personal Business Crossover)';
const ACCOUNT_TYPE_ABBREVIATIONS: Record<string, string> = {
  Checking: 'P/B Checking',
  Savings: 'P/B Savings',
  'Credit Card': 'P/B Credit Card',
};

const formatProductType = (productType: string): string => {
  if (!productType.includes(PERSONAL_BUSINESS_LABEL)) {
    return productType;
  }

  const baseType = productType.replace(PERSONAL_BUSINESS_LABEL, '').trim();

  return ACCOUNT_TYPE_ABBREVIATIONS[baseType] || productType;
};

const shouldHideSource = (dataEntry: DataEntry): boolean => {
  return !!dataEntry.journalEntry?.addOn;
};

const findSourceAccount = (dataEntry: DataEntry): LedgerAccount | null => {
  if (hasSourceInfo(dataEntry.ledgerAccount)) {
    return dataEntry.ledgerAccount;
  }

  if (!dataEntry.journalEntry?.dataEntries) {
    return null;
  }

  const otherEntry = dataEntry.journalEntry.dataEntries.find(
    entry => entry.id !== dataEntry.id && hasSourceInfo(entry.ledgerAccount),
  );

  return otherEntry?.ledgerAccount || null;
};

export const buildSourceString = (ledgerAccount: LedgerAccount | null | undefined): string => {
  if (!ledgerAccount || !hasSourceInfo(ledgerAccount)) {
    return '';
  }

  const institution = ledgerAccount.financialInstitutionProduct.financialInstitution.name;
  const productType = formatProductType(
    ledgerAccount.financialInstitutionProduct.financialInstitutionProductType?.name || '',
  );
  const lastFour = ledgerAccount.accountLastFourDigits;

  const parts = [institution, productType, lastFour].filter(Boolean);
  return parts.join(' - ');
};

export const formatSourceInfo = (dataEntry: DataEntry): string => {
  if (shouldHideSource(dataEntry)) {
    return '';
  }

  const sourceAccount = findSourceAccount(dataEntry);
  return buildSourceString(sourceAccount);
};
